import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Form from 'components/form/Form';
import RBForm from 'react-bootstrap/Form';
import FormGroup from 'components/form/FormGroup';
import API from 'core/Api';
import setServerErrors from 'utils/helpers/formErrors';
import { createErrorFromResponse } from 'core/Error';
import { useModal } from 'store/modal';
import { IInputs, IGroupOption } from '../types';

interface CandidateApplicationFormProps {
  groupOptions: IGroupOption[];
  companyName: string;
  companyTos: string;
  isMultilingual?: boolean;
}

const CandidateApplicationForm: FC<CandidateApplicationFormProps> = ({
  groupOptions,
  companyName,
  companyTos,
  isMultilingual,
}) => {
  const { t } = useTranslation();
  const methods = useForm<IInputs>();
  const { showModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = (data: IInputs) => {
    setIsLoading(true);
    return API.post('employee-candidates', {
      first_name: data.first_name,
      last_name: data.last_name,
      first_name_en: data.first_name_en,
      last_name_en: data.last_name_en,
      email: data.email,
      address: data.address,
      address_en: data.address_en,
      egn: data.egn,
      group: data.group.value,
      other_information: data.other_information,
      other_information_en: data.other_information_en,
      accept_terms: Boolean((data as any).accept_terms),
    })
      .then(res => {
        showModal({
          type: 'success',
          content: t('candidateApplication.successMessage'),
        });
        methods.reset();
      })
      .catch(err => {
        setServerErrors(createErrorFromResponse(err).errors, methods.setError);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      <Form
        methods={methods}
        onSubmit={onSubmit}
        btnLabel={t('candidateApplication.submit')}
        hideCancelBtn
        isSubmitDisabled={isLoading}
      >
        <FormGroup
          controlId="formGroupDepartment"
          label={t('candidateApplication.department')}
          type="select"
          as="select"
          name="group"
          isRequired
          selectOptions={groupOptions}
          placeholder={t('candidateApplication.selectDepartment')}
        />
        <FormGroup
          controlId="formGroupFirstName"
          label={t('structure.employees.name')}
          type="text"
          name="first_name"
          isRequired
        />
        {isMultilingual ? (
          <FormGroup
            controlId="formGroupFirstNameEn"
            label={t('structure.employees.name_en')}
            type="text"
            name="first_name_en"
            isRequired
          />
        ) : null}
        <FormGroup
          controlId="formGroupLastName"
          label={t('structure.employees.lastName')}
          type="text"
          name="last_name"
          isRequired
        />
        {isMultilingual ? (
          <FormGroup
            controlId="formGroupLastNameEn"
            label={t('structure.employees.lastName_en')}
            type="text"
            name="last_name_en"
            isRequired
          />
        ) : null}
        <FormGroup
          controlId="formGroupEmail"
          label={t('candidateApplication.email')}
          type="email"
          name="email"
          isRequired
        />
        <FormGroup
          controlId="formGroupEGN"
          label={t('candidateApplication.egn')}
          type="text"
          name="egn"
          placeholder="1234567890"
          isEGN
          isRequired
        />
        <FormGroup
          controlId="formGroupAddress"
          label={t('candidateApplication.address')}
          type="text"
          name="address"
          isRequired
        />
        {isMultilingual ? (
          <FormGroup
            controlId="formGroupAddressEn"
            label={t('candidateApplication.address_en')}
            type="text"
            name="address_en"
            isRequired
          />
        ) : null}
        <FormGroup
          controlId="formGroupOtherInformation"
          label={t('structure.employees.other')}
          type="textarea"
          name="other_information"
        />
        {isMultilingual ? (
          <FormGroup
            controlId="formGroupOtherInformationEn"
            label={t('structure.employees.other_en')}
            type="textarea"
            name="other_information_en"
          />
        ) : null}
        <div className="mb-3">
          <RBForm.Check
            custom
            type="checkbox"
            id="accept_terms"
            label={
              <span>
                {t('candidateApplication.acceptTermsPrefix')}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  style={{ marginLeft: 4 }}
                  onClick={() => {
                    showModal({
                      type: 'info',
                      size: 'lg',
                      content: (
                        <div>
                          <h2>{t('candidateApplication.termsTitle')}</h2>
                          <div dangerouslySetInnerHTML={{ __html: companyTos }} />
                        </div>
                      ),
                    });
                  }}
                >
                  {companyName}
                </button>
              </span>
            }
            {...methods.register('accept_terms', { required: true })}
          />
          {methods.formState.errors.accept_terms && (
            <RBForm.Text className="text-error">{t('candidateApplication.acceptTermsRequired')}</RBForm.Text>
          )}
        </div>
      </Form>
    </>
  );
};

export default CandidateApplicationForm;
